import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, ArrowUp, ArrowDown, Eye, EyeOff, Save, Upload, Download } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: "available", label: "Available" },
  { value: "pending", label: "Pending" },
  { value: "placed", label: "Placed" },
];

export default function AdminAvailableAnimals({ passcode }) {
  const [animals, setAnimals] = useState([]);
  const [inquiryEmail, setInquiryEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const headers = { "x-admin-passcode": passcode };

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, e] = await Promise.all([
        axios.get(`${API}/admin/available-animals`, { headers }),
        axios.get(`${API}/available-animals/inquiry-email`).then(r => r.data).catch(() => ({})),
      ]);
      setAnimals(data || []);
      setInquiryEmail(e?.email || "");
    } catch (err) {
      toast.error("Couldn't load animals.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const patch = (id, field, value) =>
    setAnimals((cur) => cur.map((a) => (a.id === id ? { ...a, [field]: value } : a)));

  const save = async (a) => {
    try {
      await axios.put(`${API}/admin/available-animals/${a.id}`, a, { headers });
      toast.success(`${a.name} saved.`);
    } catch { toast.error("Save failed."); }
  };

  const addAnimal = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/available-animals`,
        { name: "New Animal", animal_type: "", price: "" }, { headers });
      setAnimals((c) => [...c, data]);
      setOpenId(data.id);
      toast.success("Animal added.");
    } catch { toast.error("Couldn't add animal."); }
  };

  const del = async (a) => {
    if (!window.confirm(`Permanently delete ${a.name}? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/available-animals/${a.id}`, { headers });
      setAnimals((c) => c.filter((x) => x.id !== a.id));
      toast.success(`${a.name} deleted.`);
    } catch { toast.error("Delete failed."); }
  };

  const move = async (a, delta) => {
    const idx = animals.findIndex((x) => x.id === a.id);
    const target = idx + delta;
    if (target < 0 || target >= animals.length) return;
    const reordered = [...animals];
    reordered.splice(target, 0, reordered.splice(idx, 1)[0]);
    setAnimals(reordered);
    try {
      await axios.post(`${API}/admin/available-animals/reorder`,
        { order: reordered.map((x) => x.id) }, { headers });
    } catch { toast.error("Reorder failed."); }
  };

  const uploadTo = async (path, file, extraForm = {}, onDone) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    for (const [k, v] of Object.entries(extraForm)) fd.append(k, v);
    try {
      const { data } = await axios.post(`${API}${path}`, fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      onDone && onDone(data);
      toast.success("Uploaded.");
    } catch (err) {
      // Surface the real server-side reason so the user isn't stuck guessing.
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      let msg;
      if (detail && typeof detail === "string") {
        msg = detail;
      } else if (status === 401) {
        msg = "Not signed in — please log into the admin again.";
      } else if (status === 413) {
        msg = "That file is too large.";
      } else if (status === 415) {
        msg = "That file type isn't supported.";
      } else if (!status) {
        msg = "Couldn't reach the server. Please check your connection and try again.";
      } else {
        msg = `Upload failed (HTTP ${status}).`;
      }
      toast.error(msg, { duration: 7000 });
    }
  };

  const saveInquiryEmail = async () => {
    try {
      await axios.put(`${API}/admin/settings/inquiry-email`, { email: inquiryEmail }, { headers });
      toast.success("Inquiry email saved.");
    } catch { toast.error("Save failed."); }
  };

  if (loading) return <p className="text-[#6D5A50]">Loading available animals…</p>;

  return (
    <div data-testid="admin-available-animals" className="space-y-6">
      {/* Inquiry email setting */}
      <div className="parchment-deep border border-[#A87149]/40 rounded-md p-4">
        <label className="block font-body text-sm text-[#4B3A2F] mb-2">Inquiry email (used by all Inquire buttons)</label>
        <div className="flex gap-2">
          <input
            value={inquiryEmail}
            onChange={(e) => setInquiryEmail(e.target.value)}
            className="flex-1 px-3 py-2 rounded border border-[#A87149]/50 bg-white font-body text-sm"
            placeholder="you@example.com"
          />
          <button onClick={saveInquiryEmail}
            className="px-4 py-2 rounded bg-[#4B3A2F] text-[#F7F0E4] font-body text-sm flex items-center gap-1">
            <Save size={14} /> Save
          </button>
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={addAnimal}
          className="px-4 py-2 rounded-full bg-[#3E8054] text-white font-body text-sm flex items-center gap-2">
          <Plus size={16} /> Add Animal Listing
        </button>
      </div>

      {animals.map((a, i) => (
        <div key={a.id} className={`bg-white border rounded-md ${a.hidden ? "border-[#A87149]/60 opacity-70" : "border-[#E7DFD5]"} page-shadow`}>
          <div className="p-4 flex flex-wrap items-center gap-3">
            <button onClick={() => setOpenId(openId === a.id ? null : a.id)}
              className="text-left flex-1 font-display text-xl text-[#4B3A2F] hover:underline">
              {i + 1}. {a.name || "(untitled)"}<span className="text-sm text-[#8D6E63] font-body italic ml-2">— {a.animal_type} · {a.price} · {a.status}</span>
            </button>
            <button onClick={() => move(a, -1)} title="Move up" className="p-2 rounded border border-[#A87149]/40 hover:bg-[#F5EFE1]"><ArrowUp size={14} /></button>
            <button onClick={() => move(a, 1)} title="Move down" className="p-2 rounded border border-[#A87149]/40 hover:bg-[#F5EFE1]"><ArrowDown size={14} /></button>
            <button onClick={async () => { const next = !a.hidden; patch(a.id, "hidden", next); await save({ ...a, hidden: next }); }}
              title={a.hidden ? "Show" : "Hide"} className="p-2 rounded border border-[#A87149]/40 hover:bg-[#F5EFE1]">
              {a.hidden ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
            <button onClick={() => del(a)} title="Delete" className="p-2 rounded border border-[#B33A2A]/40 text-[#B33A2A] hover:bg-[#F5EFE1]"><Trash2 size={14} /></button>
          </div>

          {openId === a.id && (
            <div className="p-4 sm:p-6 border-t border-[#E7DFD5] space-y-4 font-body text-sm">
              {/* Basic fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Name">
                  <input value={a.name || ""} onChange={(e) => patch(a.id, "name", e.target.value)} className={inputCls} />
                </Field>
                <Field label="Animal type">
                  <input value={a.animal_type || ""} onChange={(e) => patch(a.id, "animal_type", e.target.value)} className={inputCls} />
                </Field>
                <Field label="Price">
                  <input value={a.price || ""} onChange={(e) => patch(a.id, "price", e.target.value)} className={inputCls} placeholder="$12,000" />
                </Field>
                <Field label="Price note (optional)">
                  <input value={a.price_note || ""} onChange={(e) => patch(a.id, "price_note", e.target.value)} className={inputCls} placeholder="combined" />
                </Field>
                <Field label="Status">
                  <select value={a.status} onChange={(e) => patch(a.id, "status", e.target.value)} className={inputCls}>
                    {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </Field>
                <Field label="Bonded pair?">
                  <label className="flex items-center gap-2 py-2">
                    <input type="checkbox" checked={!!a.is_bonded_pair} onChange={(e) => patch(a.id, "is_bonded_pair", e.target.checked)} />
                    <span className="text-[#4B3A2F]">Yes, offered only as a pair</span>
                  </label>
                </Field>
                {a.is_bonded_pair && (
                  <Field label="Bonded pair notice">
                    <input value={a.bonded_notice || ""} onChange={(e) => patch(a.id, "bonded_notice", e.target.value)} className={inputCls}
                           placeholder="BONDED PAIR — NOT OFFERED SEPARATELY" />
                  </Field>
                )}
              </div>
              <Field label="Description">
                <textarea value={a.description || ""} onChange={(e) => patch(a.id, "description", e.target.value)} rows={3}
                          className={`${inputCls} resize-y`} placeholder="Optional story / temperament / notes" />
              </Field>

              <button onClick={() => save(a)} className="px-4 py-2 rounded bg-[#4B3A2F] text-[#F7F0E4] flex items-center gap-2">
                <Save size={14} /> Save details
              </button>

              {/* Featured photo */}
              <hr className="border-[#E7DFD5]" />
              <SubSection title="Featured photo">
                {a.featured_photo && (
                  <img
                    src={`${BACKEND_URL}${a.featured_photo}`}
                    alt=""
                    className="max-w-[240px] w-full h-auto rounded border border-[#A87149]/40"
                  />
                )}
                <UploadBtn
                  label={a.featured_photo ? "Replace featured photo" : "Upload featured photo"}
                  accept="image/*"
                  onFile={(f) => uploadTo(`/admin/available-animals/${a.id}/featured-photo`, f, {}, () => load())}
                />
              </SubSection>

              {/* FAQ pages — variable number of flyer pages */}
              <SubSection title="FAQ flyer pages (upload as many JPG pages as this animal needs)">
                <div className="w-full space-y-3">
                  {(a.faq_pages || []).filter(Boolean).length === 0 && (
                    <p className="text-xs text-[#6D5A50] italic">No flyer pages yet. Use "Add Flyer Page" below to upload the first page.</p>
                  )}
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {(a.faq_pages || []).filter(Boolean).map((url, i, arr) => (
                      <div key={`${url}-${i}`} className="text-center border border-[#E7DFD5] rounded p-2 bg-[#FBF8F1]">
                        <img
                          src={`${BACKEND_URL}${url}`}
                          alt={`FAQ page ${i + 1}`}
                          className="w-full aspect-[8.5/11] object-contain bg-[#F5EFE1] border border-[#A87149]/40 rounded"
                        />
                        <p className="mt-1 font-body text-xs text-[#4B3A2F]">Page {i + 1} of {arr.length}</p>
                        <div className="mt-1 flex justify-center gap-1 flex-wrap">
                          <button
                            title="Move up"
                            disabled={i === 0}
                            onClick={async () => {
                              const next = [...arr];
                              [next[i - 1], next[i]] = [next[i], next[i - 1]];
                              try {
                                await axios.post(`${API}/admin/available-animals/${a.id}/faq-pages/reorder`,
                                  { order: next }, { headers });
                                load();
                              } catch { toast.error("Reorder failed."); }
                            }}
                            className="px-1 rounded border border-[#A87149]/40 disabled:opacity-30"
                          ><ArrowUp size={12} /></button>
                          <button
                            title="Move down"
                            disabled={i === arr.length - 1}
                            onClick={async () => {
                              const next = [...arr];
                              [next[i + 1], next[i]] = [next[i], next[i + 1]];
                              try {
                                await axios.post(`${API}/admin/available-animals/${a.id}/faq-pages/reorder`,
                                  { order: next }, { headers });
                                load();
                              } catch { toast.error("Reorder failed."); }
                            }}
                            className="px-1 rounded border border-[#A87149]/40 disabled:opacity-30"
                          ><ArrowDown size={12} /></button>
                          <label
                            title="Replace this page"
                            className="px-1 rounded border border-[#A87149]/40 cursor-pointer hover:bg-[#F5EFE1]"
                          >
                            <Upload size={12} />
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                e.target.value = "";
                                if (!file) return;
                                const fd = new FormData();
                                fd.append("file", file);
                                try {
                                  await axios.put(`${API}/admin/available-animals/${a.id}/faq-pages/${i}`,
                                    fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
                                  toast.success(`Page ${i + 1} replaced.`);
                                  load();
                                } catch { toast.error("Replace failed."); }
                              }}
                            />
                          </label>
                          <button
                            title="Delete this page"
                            onClick={async () => {
                              if (!window.confirm(`Delete Page ${i + 1}?`)) return;
                              try {
                                await axios.delete(`${API}/admin/available-animals/${a.id}/faq-pages/${i}`, { headers });
                                toast.success("Page deleted.");
                                load();
                              } catch { toast.error("Delete failed."); }
                            }}
                            className="px-1 rounded border border-[#B33A2A]/40 text-[#B33A2A]"
                          ><Trash2 size={12} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <UploadBtn
                    label="Add Flyer Page"
                    accept="image/*"
                    onFile={(f) => uploadTo(`/admin/available-animals/${a.id}/faq-pages`, f, {}, () => load())}
                  />
                </div>
              </SubSection>

              {/* FAQ PDF */}
              <SubSection title="Original FAQ PDF (used by the Download FAQ button)">
                {a.faq_pdf && (
                  <a href={`${BACKEND_URL}${a.faq_pdf}`} target="_blank" rel="noopener noreferrer"
                     className="inline-flex items-center gap-1 text-[#4B3A2F] underline">
                    <Download size={14} /> current PDF
                  </a>
                )}
                <UploadBtn label={a.faq_pdf ? "Replace FAQ PDF" : "Upload FAQ PDF"} accept="application/pdf"
                  onFile={(f) => uploadTo(`/admin/available-animals/${a.id}/faq-pdf`, f, {}, () => load())} />
              </SubSection>

              {/* Gallery */}
              <SubSection title="More Photos gallery">
                <UploadBtn label="Add photo (upload multiple to add several)" accept="image/*" multiple
                  onFile={async (files) => {
                    const list = files.length ? Array.from(files) : [files];
                    for (const f of list) {
                      await uploadTo(`/admin/available-animals/${a.id}/gallery`, f, {}, null);
                    }
                    load();
                  }}
                />
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 w-full mt-2">
                  {(a.gallery || []).slice().sort((x, y) => (x.order ?? 0) - (y.order ?? 0)).map((p, idx, arr) => (
                    <div key={p.id} className="text-center">
                      <img
                        src={`${BACKEND_URL}${p.url}`}
                        alt=""
                        className="w-full h-auto rounded border border-[#A87149]/40"
                      />
                      <input
                        placeholder="Caption (optional)"
                        defaultValue={p.caption || ""}
                        onBlur={async (e) => {
                          try {
                            await axios.put(`${API}/admin/available-animals/${a.id}/gallery/${p.id}`,
                              { caption: e.target.value }, { headers });
                          } catch { toast.error("Caption failed."); }
                        }}
                        className="mt-1 w-full px-2 py-1 text-xs border border-[#A87149]/40 rounded"
                      />
                      <div className="mt-1 flex justify-center gap-1">
                        <button title="Move up" disabled={idx === 0} onClick={async () => {
                          const ids = arr.map((x) => x.id);
                          [ids[idx - 1], ids[idx]] = [ids[idx], ids[idx - 1]];
                          await axios.post(`${API}/admin/available-animals/${a.id}/gallery/reorder`, { order: ids }, { headers });
                          load();
                        }} className="px-1 rounded border border-[#A87149]/40 disabled:opacity-30"><ArrowUp size={12} /></button>
                        <button title="Move down" disabled={idx === arr.length - 1} onClick={async () => {
                          const ids = arr.map((x) => x.id);
                          [ids[idx + 1], ids[idx]] = [ids[idx], ids[idx + 1]];
                          await axios.post(`${API}/admin/available-animals/${a.id}/gallery/reorder`, { order: ids }, { headers });
                          load();
                        }} className="px-1 rounded border border-[#A87149]/40 disabled:opacity-30"><ArrowDown size={12} /></button>
                        <button title="Set as first"
                          disabled={idx === 0}
                          onClick={async () => {
                            const ids = arr.map((x) => x.id).filter((id) => id !== p.id);
                            ids.unshift(p.id);
                            await axios.post(`${API}/admin/available-animals/${a.id}/gallery/reorder`, { order: ids }, { headers });
                            load();
                          }}
                          className="px-1 text-xs border border-[#A87149]/40 rounded disabled:opacity-30">1st</button>
                        <button title="Delete" onClick={async () => {
                          if (!window.confirm("Remove this photo?")) return;
                          await axios.delete(`${API}/admin/available-animals/${a.id}/gallery/${p.id}`, { headers });
                          load();
                        }} className="px-1 rounded border border-[#B33A2A]/40 text-[#B33A2A]"><Trash2 size={12} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </SubSection>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

const inputCls = "w-full px-3 py-2 rounded border border-[#A87149]/50 bg-white font-body text-sm text-[#2C2724]";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wider text-[#6D5A50] mb-1">{label}</span>
      {children}
    </label>
  );
}

function SubSection({ title, children }) {
  return (
    <div className="space-y-2">
      <p className="font-display text-lg text-[#4B3A2F]">{title}</p>
      <div className="flex flex-wrap items-center gap-3">{children}</div>
    </div>
  );
}

function UploadBtn({ label, accept, onFile, multiple = false, size = "md" }) {
  const cls = size === "sm"
    ? "mt-1 inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-[#D6BFA0] text-[#4B3A2F] border border-[#A87149] cursor-pointer hover:bg-[#C8A876]"
    : "inline-flex items-center gap-2 px-3 py-2 rounded bg-[#D6BFA0] text-[#4B3A2F] border border-[#A87149] cursor-pointer hover:bg-[#C8A876] text-sm";
  return (
    <label className={cls}>
      <Upload size={14} /> {label}
      <input
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={(e) => {
          const files = e.target.files;
          if (!files || files.length === 0) return;
          onFile(multiple ? files : files[0]);
          e.target.value = "";
        }}
      />
    </label>
  );
}
